import React, { useState, useCallback } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { IconButton, Tooltip as MuiTooltip } from '@mui/material';
import JsonSyntaxHighlighter from './JsonSyntaxHighlighter';
import ResourceTable from './ResourceTable';
import ActionBar from './ActionBar';
import WarehouseLocations from './WarehouseLocations';
import { STATUS_BADGE, STATUS_LABEL } from './sandboxConfig';

function getRowTitle(row) {
  return row.order_number || row.purchase_order_number
    || row.invoice_number || row.sku || row.name || row.file_name || row.id;
}

function JsonResponsePanel({
  row,
  sandboxType,
  resourceKey,
  apiPath,
  connectionId,
  onRefresh,
  onUpdateClick,
  onModalAction,
}) {
  const [copied, setCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [viewMode, setViewMode] = useState('json');
  // null = default (depth 2), true = expanded (999), false = collapsed (1)
  const [expandState, setExpandState] = useState(null);
  const [expandKey, setExpandKey] = useState(0);

  const toggleExpandAll = useCallback(() => {
    setExpandState((prev) => prev !== true);
    setExpandKey((k) => k + 1);
  }, []);

  if (!row) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface-100/60">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-surface-100 flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-surface-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
          <p className="text-sm text-surface-400">Select a resource to view its API response</p>
        </div>
      </div>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(row, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      {/* header */}
      <div className="border-b border-surface-200 px-5 py-3 bg-surface-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-surface-900 truncate">
            {getRowTitle(row)}
          </span>
          {row.status && (
            <span className={`badge status-badge ${STATUS_BADGE[row.status] || 'badge-neutral'} text-[10px] py-0 flex-shrink-0`}>
              {STATUS_LABEL[row.status] || row.status}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-1 group">
          <span className="font-mono text-[11px] text-surface-400 truncate">
            {`${process.env.REACT_APP_API_ENDPOINT}/${apiPath}/${row.id}`}
          </span>
          <MuiTooltip
            title={urlCopied ? 'Copied!' : 'Copy URL'}
            arrow
            placement="right"
          >
            <IconButton
              size="small"
              onClick={() => {
                const url = `${process.env.REACT_APP_API_ENDPOINT}/${apiPath}/${row.id}`;
                navigator.clipboard.writeText(url);
                setUrlCopied(true);
                setTimeout(() => setUrlCopied(false), 2000);
              }}
              className="opacity-0 group-hover:opacity-100 text-surface-400 hover:text-surface-600"
              sx={{ padding: '2px' }}
            >
              <ContentCopyIcon sx={{ fontSize: '11px' }} />
            </IconButton>
          </MuiTooltip>
        </div>
      </div>

      {/* actions */}
      <ActionBar
        row={row}
        sandboxType={sandboxType}
        resourceKey={resourceKey}
        connectionId={connectionId}
        onRefresh={onRefresh}
        onUpdateClick={onUpdateClick}
        onModalAction={onModalAction}
      />

      {resourceKey === 'warehouses' && (
        <WarehouseLocations
          warehouseId={row.id}
          connectionId={connectionId}
        />
      )}

      {/* body */}
      <div className="flex-1 overflow-y-scroll px-5 py-4 bg-surface-100/60 relative min-w-0">
        <div className="absolute top-2 right-3 z-10 flex items-center gap-1">
          <div className="flex items-center gap-0.5 border border-surface-200 rounded-md p-0.5 bg-white">
            <MuiTooltip title="JSON" arrow placement="top">
              <button
                type="button"
                onClick={() => setViewMode('json')}
                className={`px-1.5 py-1 rounded text-xs font-mono leading-none transition-colors ${
                  viewMode === 'json'
                    ? 'bg-surface-100 text-surface-900'
                    : 'text-surface-400 hover:text-surface-600'
                }`}
              >
                {'{ }'}
              </button>
            </MuiTooltip>
            <MuiTooltip title="Table" arrow placement="top">
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`px-1.5 py-1 rounded leading-none transition-colors ${
                  viewMode === 'table'
                    ? 'bg-surface-100 text-surface-900'
                    : 'text-surface-400 hover:text-surface-600'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18M10 3v18M14 3v18M3 6a3 3 0 013-3h12a3 3 0 013 3v12a3 3 0 01-3 3H6a3 3 0 01-3-3V6z" />
                </svg>
              </button>
            </MuiTooltip>
          </div>
          <IconButton
            size="small"
            onClick={viewMode === 'json' ? toggleExpandAll : undefined}
            className="text-surface-400 hover:text-surface-600 hover:bg-surface-100"
            sx={{ padding: '4px', visibility: viewMode === 'json' ? 'visible' : 'hidden' }}
          >
            {expandState === true ? (
              <UnfoldLessIcon sx={{ fontSize: '14px' }} />
            ) : (
              <UnfoldMoreIcon sx={{ fontSize: '14px' }} />
            )}
          </IconButton>
          <MuiTooltip title={copied ? 'Copied!' : 'Copy JSON'} arrow placement="top">
            <IconButton
              size="small"
              onClick={handleCopy}
              className="text-surface-400 hover:text-surface-600 hover:bg-surface-100"
              sx={{ padding: '4px' }}
            >
              <ContentCopyIcon sx={{ fontSize: '14px' }} />
            </IconButton>
          </MuiTooltip>
        </div>
        {viewMode === 'json' ? (
          <JsonSyntaxHighlighter
            data={row}
            // eslint-disable-next-line no-nested-ternary
            defaultExpandDepth={expandState === true ? 999 : expandState === false ? 1 : 2}
            expandKey={expandKey}
          />
        ) : (
          <ResourceTable row={row} resourceKey={resourceKey} />
        )}
      </div>
    </div>
  );
}

export default JsonResponsePanel;
